// Stateless presentational helpers shared across the Territory Overview card and its panels.
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";

import {
  BORDER,
  INPUT_BADGE_BG,
  INPUT_BADGE_TEXT,
  MUTED,
  PRIMARY_BLUE,
  TRACK_BG,
  TWO_BAR_PANEL_HEIGHT,
} from "./tokens";

export function AutoSyncedPill({ lastRefreshTime }) {
  const formattedTime = lastRefreshTime
    ? moment(lastRefreshTime).utcOffset(-5).format("MM/DD/YYYY hh:mm A")
    : null;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        borderRadius: "999px",
        bgcolor: TRACK_BG,
        px: 1,
        py: 0.25,
      }}
    >
      <RefreshIcon sx={{ fontSize: 12, color: MUTED }} />

      <Typography
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: MUTED,
          lineHeight: 1.2,
        }}
      >
        {formattedTime ? `Auto-synced at ${formattedTime} EST` : "Auto-synced"}
      </Typography>
    </Box>
  );
}

export function SectionLabel({ children, sx }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: MUTED,
        mb: 1,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export function ExploreButton({ onClick }) {
  if (typeof onClick !== "function") return null;
  return (
    <Button
      variant="text"
      size="small"
      onClick={() => onClick()}
      sx={{
        textTransform: "none",
        fontSize: 14,
        fontWeight: 600,
        color: "#D96B04",
        minWidth: "auto",
        p: 0,
        flexShrink: 0,
      }}
    >
      Explore
    </Button>
  );
}

export function SectionTitleRow({ label, onExplore, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 1,
        width: "100%",
        ...sx,
      }}
    >
      <SectionLabel sx={{ mb: 0, fontSize: 12 }}>{label}</SectionLabel>
      <ExploreButton onClick={onExplore} />
    </Box>
  );
}

export function NestedPanel({ children, sx }) {
  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "8px",
        p: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function EmptyPanelMessage({ children }) {
  return (
    <Typography sx={{ fontSize: 12, color: MUTED, textAlign: "center" }}>
      {children}
    </Typography>
  );
}

/** Fixed-height panel whose content stays vertically centered. */
export function TwoBarPanel({ label, onExplore, children }) {
  return (
    <NestedPanel
      sx={{
        minHeight: TWO_BAR_PANEL_HEIGHT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SectionTitleRow label={label} onExplore={onExplore} />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </NestedPanel>
  );
}

// Small pill badge marking a field as rep-editable ("✎ Your input"); shared for reuse by other author-input fields in Section 1.
export function YourInputBadge() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        borderRadius: "999px",
        bgcolor: INPUT_BADGE_BG,
        color: INPUT_BADGE_TEXT,
        px: 0.75,
        py: 0.25,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.2,
      }}
    >
      <EditOutlinedIcon sx={{ fontSize: 12 }} />
      Your input
    </Box>
  );
}

/** Unobtrusive "Saving…" / "Saved" / "Save failed" indicator for an input field. */
export function SaveStatus({ status }) {
  if (status !== "saving" && status !== "saved" && status !== "error")
    return null;

  const label =
    status === "saving"
      ? "Saving…"
      : status === "saved"
        ? "Saved"
        : "Save failed";
  // Muted gray while in flight, primary blue once confirmed, MUI's error red on failure.
  const color =
    status === "saving" ? MUTED : status === "saved" ? PRIMARY_BLUE : "#D32F2F";

  return (
    <Typography
      component="span"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  );
}
